import { MailOutlined, MessageOutlined } from "@ant-design/icons"
import { Button, Dropdown, Tooltip } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { unavailableProps } from "../../../utils/unavailable-control.ts"
import { useCommunicationRights } from "../communication-permissions.ts"
import { useComposerLauncher } from "../composer/CommunicateButton.tsx"
import { targetToPrefill } from "../composer/composer-target.ts"
import { onMessagesSent } from "../composer/messages-sent.ts"
import type { Channel } from "../types/compose.types.ts"
import { useContactConsent } from "./consent-store.ts"
import { summarizeChannel } from "./consent-summary.ts"
import {
  type CommContact,
  type CommRecord,
  contactKey
} from "./record-contacts.ts"

/** Blocks that hold for every contact, not just this one: the company cannot send on the channel. */
const CHANNEL_WIDE = new Set(["smsNotSetUp"])

const CHANNEL_ICON: Record<Channel, React.ReactNode> = {
  email: <MailOutlined aria-hidden />,
  sms: <MessageOutlined aria-hidden />
}

/** Why one contact cannot be written to on `channel`, from their consent; `null` when they can, or before it is known. */
export const useSendBlock = (
  contact: CommContact | null,
  channel: Channel
): string | null => {
  const { load } = useContactConsent(contact?.ref ?? null)
  if (load.state !== "ready") return null
  return summarizeChannel(load.consent, channel).blocked
}

const ContactMenuLabel = ({
  contact,
  channel
}: {
  contact: CommContact
  channel: Channel
}) => {
  const { t } = useTranslation()
  const block = useSendBlock(contact, channel)
  return (
    <span className="flex flex-col">
      <span>
        {contact.name || t("recordComm.contact.unnamed")}{" "}
        <span className="text-xs text-slate-500">
          · {t(`recordComm.contact.role.${contact.role}`)}
        </span>
      </span>
      {block && (
        <span className="text-xs text-slate-500">
          {t(`recordComm.status.${block}`)}
        </span>
      )}
    </span>
  )
}

const ChannelButton = ({
  channel,
  record,
  contacts,
  doNotContact,
  showLabel,
  onOpen,
  loading,
  size
}: {
  size: "middle" | "large"
  channel: Channel
  record: CommRecord
  contacts: CommContact[]
  doNotContact: boolean
  showLabel: boolean
  onOpen: (channel: Channel, contact: CommContact) => void
  loading: boolean
}) => {
  const { t } = useTranslation()
  const rights = useCommunicationRights()
  const primary = contacts[0] ?? null
  const primaryBlock = useSendBlock(primary, channel)
  const actionLabel = t(`recordComm.header.send.${channel}`)
  const many = contacts.length > 1

  const reason = !rights.send
    ? t("recordComm.header.noSendRight")
    : doNotContact
      ? t("recordComm.status.doNotContact")
      : !primary
        ? t("recordComm.header.noContact", {
            record: t(`recordComm.noun.${record.type}`)
          })
        : primaryBlock && (!many || CHANNEL_WIDE.has(primaryBlock))
          ? t(`recordComm.status.${primaryBlock}`)
          : null

  const unavailable = unavailableProps(
    !!reason,
    many ? undefined : () => primary && onOpen(channel, primary)
  )
  const button = (
    <Button
      size={size}
      aria-label={actionLabel}
      loading={loading}
      icon={CHANNEL_ICON[channel]}
      {...unavailable}
      className={`rounded-lg ${unavailable.className ?? ""}`}
    >
      {showLabel && (
        <span className="hidden text-xs sm:inline">
          {t(`recordComm.header.label.${channel}`)}
        </span>
      )}
    </Button>
  )

  if (reason)
    return <Tooltip title={`${actionLabel}: ${reason}`}>{button}</Tooltip>
  if (!many) return <Tooltip title={actionLabel}>{button}</Tooltip>
  return (
    <Dropdown
      trigger={["click"]}
      menu={{
        items: [
          {
            key: "title",
            type: "group",
            label: t("recordComm.header.chooseRecipient")
          },
          ...contacts.map((contact) => ({
            key: contactKey(contact.ref),
            label: <ContactMenuLabel contact={contact} channel={channel} />
          }))
        ],
        onClick: ({ key }) => {
          const contact = contacts.find((item) => contactKey(item.ref) === key)
          if (contact) onOpen(channel, contact)
        }
      }}
    >
      <Tooltip title={actionLabel} placement="top">
        {button}
      </Tooltip>
    </Dropdown>
  )
}

/**
 * The record header's Send email / Send SMS buttons. Each opens the shared
 * composer on that channel, related to this record and addressed to its
 * contact; with several contacts the sender picks one first. The composer
 * itself still applies every consent, configuration and permission check.
 */
export const RecordSendActions = ({
  record,
  contacts,
  doNotContact = false,
  showLabels = true,
  size = "middle",
  onSent
}: {
  size?: "middle" | "large"
  record: CommRecord
  contacts: CommContact[]
  doNotContact?: boolean
  showLabels?: boolean
  onSent?: () => void
}) => {
  const composer = useComposerLauncher(onSent)
  const rights = useCommunicationRights()
  useEffect(() => (onSent ? onMessagesSent(onSent) : undefined), [onSent])
  if (!rights.send && !rights.viewAll) return null

  const open = (channel: Channel, contact: CommContact) =>
    composer.open(
      targetToPrefill({
        record: { type: record.type, id: record.id },
        recordLabel: record.label,
        recipient: contact.ref,
        recipientName: contact.name,
        channel
      })
    )

  return (
    <span className="inline-flex items-center gap-2">
      {(["email", "sms"] as Channel[]).map((channel) => (
        <ChannelButton
          key={channel}
          channel={channel}
          record={record}
          contacts={contacts}
          doNotContact={doNotContact}
          showLabel={showLabels}
          onOpen={open}
          loading={composer.loading}
          size={size}
        />
      ))}
      {composer.element}
    </span>
  )
}
